// @boty/ai - SSE streaming utilities
// Provides server-sent events streaming for chat responses

/**
 * SSE event data structure for chat streaming
 */
export interface ChatStreamEvent {
  /** Chunk of response text */
  content: string;
}

/**
 * SSE event for stream errors
 */
export interface StreamErrorEvent {
  error: {
    code: string;
    message: string;
  };
}

/**
 * Creates an SSE-formatted string from data
 * @param data - The data to encode as SSE event
 * @returns SSE-formatted string
 */
export function encodeSSEEvent(data: ChatStreamEvent | StreamErrorEvent | '[DONE]'): string {
  if (data === '[DONE]') {
    return 'data: [DONE]\n\n';
  }
  return `data: ${JSON.stringify(data)}\n\n`;
}

/**
 * Creates a ReadableStream that streams chunks as SSE events
 * @param asyncIterable - Async iterable of string chunks (from OpenAI stream)
 * @param options - Configuration options
 * @returns ReadableStream for Response
 */
export function createStreamingResponse(
  asyncIterable: AsyncIterable<string>,
  options?: {
    /** Callback when stream completes with full response */
    onComplete?: (fullResponse: string) => Promise<void>;
    /** Callback when error occurs */
    onError?: (error: Error) => void | Promise<void>;
  }
): ReadableStream<Uint8Array> {
  const encoder = new TextEncoder();
  let fullResponse = '';

  return new ReadableStream({
    async start(controller) {
      try {
        for await (const chunk of asyncIterable) {
          fullResponse += chunk;
          const event = encodeSSEEvent({ content: chunk });
          controller.enqueue(encoder.encode(event));
        }

        // Send done event so client knows stream is complete
        controller.enqueue(encoder.encode(encodeSSEEvent('[DONE]')));

        // Await completion handler (DB save + PostHog flush) before closing —
        // Cloudflare Workers terminate after stream closes, killing pending async work
        if (options?.onComplete) {
          await options.onComplete(fullResponse);
        }

        controller.close();
      } catch (error) {
        // Send error event
        const errorEvent = encodeSSEEvent({
          error: {
            code: 'STREAM_ERROR',
            message: error instanceof Error ? error.message : 'Unknown streaming error',
          },
        });
        controller.enqueue(encoder.encode(errorEvent));

        // Await error handler (error capture + PostHog flush) before closing —
        // same serverless-freeze concern as onComplete above
        if (options?.onError && error instanceof Error) {
          await options.onError(error);
        }

        controller.close();
      }
    },
  });
}

/**
 * SSE Response headers type
 */
export interface SSEHeaders {
  [key: string]: string;
  'Content-Type': string;
  'Cache-Control': string;
  Connection: string;
  'X-Accel-Buffering': string;
}

/**
 * Creates Response headers for SSE streaming
 */
export function getSSEHeaders(): SSEHeaders {
  return {
    'Content-Type': 'text/event-stream',
    'Cache-Control': 'no-cache, no-transform',
    Connection: 'keep-alive',
    'X-Accel-Buffering': 'no', // Disable nginx buffering
  };
}

/**
 * Extracts content chunks from OpenAI streaming response
 * Yields only non-empty content strings
 */
export async function* extractStreamContent(
  stream: AsyncIterable<{ choices: Array<{ delta: { content?: string | null } }> }>
): AsyncIterable<string> {
  for await (const chunk of stream) {
    const content = chunk.choices[0]?.delta?.content;
    if (content) {
      yield content;
    }
  }
}
